import { IListingState } from "contexts/ListingContextHelpers/interfaces";
import {
  findPostsClientSide,
  getRecommendedPostsClientSide,
} from "dataLayer/apisWithCache/postsApis/postSearch";
import { processFacets } from "contexts/ListingContextHelpers/reducerHelpers";
import { isCategoryRealEstate } from "helpers/osHelpers";
import { fetchSeoClientSide } from "dataLayer/apisWithCache/seoApis/seoWidgetApi";
import { getLang, getLang2 } from "helpers/themeHelpers";
import { ILiteRouterApiResponse } from "models/ILiteRouterApiResponse";
import { EnumScreenNames } from "../../enums/EnumScreenNames";
import { __getAjaxClient } from "utils/misc/fetchClient";
import { ICategoryLiteAsset } from "models/Assets/ICategoryAsset";
import qs from "qs";
import {getSimilarAdsClientSide} from "dataLayer/apisWithCache/postsApis/similarAds";
import {POSTS_MAX_PER_PAGE} from "utils/constants";
import { getRecommendedAdsClientSide } from "dataLayer/apisWithCache/postsApis/recommendedAds";

let abortController__requestPostsApi,
  abortController__requestRecommendedAds,
  abortController__requestSubCats,
  abortController__requestSeo;

export const requestPostsApi = async (
  prevState: IListingState
): Promise<IListingState> => {
  if (abortController__requestPostsApi) {
    abortController__requestPostsApi.abort();
    abortController__requestPostsApi = null;
  }

  abortController__requestPostsApi = new AbortController();


  if (prevState.queryString?.similarSerp) {
    const postId = prevState.queryString?.similarSerp;
    const page = prevState?.pageNumber;
    const device = prevState?.device;
    prevState.postsApiResponse = await getSimilarAdsClientSide(postId, page, POSTS_MAX_PER_PAGE[device], true);
  } else if (prevState.queryString?.recommendedSerp) {
    const postId = prevState.queryString?.recommendedSerp;
    const page = prevState?.pageNumber;
    const device = prevState?.device;
    prevState.postsApiResponse = await getRecommendedAdsClientSide(postId, page, POSTS_MAX_PER_PAGE[device], true, prevState.queryString?.includeSelf ?? 0);
  } else {
    prevState.postsApiResponse = await findPostsClientSide(
      prevState.queryString,
      null,
      prevState.currency,
      abortController__requestPostsApi
    );
  }

  prevState.facets = processFacets(
    prevState.postsApiResponse._meta,
    prevState.assets,
    prevState.category,
    prevState.subCategory,
    prevState.basePath
  );
  prevState.assets.filterFlags =
    prevState.postsApiResponse._meta.filter_options;
  return prevState;
};

export const requestRecommendedAds = async (
  prevState: IListingState
): Promise<IListingState> => {
  if (abortController__requestRecommendedAds) {
    abortController__requestRecommendedAds.abort();
    abortController__requestRecommendedAds = null;
  }
  abortController__requestRecommendedAds = new AbortController();
  const { queryString } = prevState;
  prevState.recommendedAds = [];
  if (
    prevState.postsApiResponse._meta.pageCount ===
      prevState.postsApiResponse._meta.currentPage ||
    prevState.postsApiResponse._meta.pageCount === 0
  ) {
    const qsForRecommendedPosts = JSON.parse(JSON.stringify(queryString));
    qsForRecommendedPosts.PostSearch["ignoreIds"] = (
      (prevState?.postsApiResponse.items || []).map((post) => post.id) || []
    ).join(",");

    prevState.recommendedAds = await getRecommendedPostsClientSide(
      qsForRecommendedPosts,
      abortController__requestRecommendedAds
    ).then((data) => data.items);
  }
  return prevState;
};

export const requestSubCats = async (
  prevState: IListingState,
  categoryId: number | string
): Promise<IListingState> => {
  if (abortController__requestSubCats) {
    abortController__requestSubCats.abort();
    abortController__requestSubCats = null;
  }
  abortController__requestSubCats = new AbortController();

  const subCategories = await __getAjaxClient<ICategoryLiteAsset[]>(
    `/requestSubCats?categoryId=${categoryId}`,
    abortController__requestSubCats
  );
  prevState.assets.subCategories = subCategories;
  return prevState;
};

export const requestSeo = async (
  prevState: IListingState
): Promise<IListingState> => {
  if (abortController__requestSeo) {
    abortController__requestSeo.abort();
    abortController__requestSeo = null;
  }
  abortController__requestSeo = new AbortController();
  const {
    category,
    subCategory,
    city,
    neighborhoods,
    selectedCpsOptions,
    assets,
  } = prevState;
  let nHood = null,
    ops = [];
  if (isCategoryRealEstate(category)) {
    nHood = neighborhoods?.[0] || null;
  }
  if (selectedCpsOptions?.length > 0) {
    const linkableAttrs = assets.dynamicAttributeEnhancedAssets.filter(
      (attr) => attr.field.is_linkable
    );
    let prevOps = undefined;
    for (const attr of linkableAttrs) {
      if (prevOps === null) break;

      const currentOps = selectedCpsOptions.filter(
          (o) => o.fieldName === attr.fieldName
      );

      switch (currentOps.length) {
        case 1:
          prevOps = currentOps;
          ops.push(...currentOps);
          break;
        case 0:
          prevOps = null;
          break;
        default:
          ops.push(currentOps[0]);
          prevOps = null;
          break;
      }
    }
  }

  const locale = getLang();
  const routerApiResponse: ILiteRouterApiResponse = {
    cityId: Number(city?.id) || null,
    neighborhoodId: Number(neighborhoods?.[0]?.id) || null,
    categoryId: Number(category?.id) || null,
    subCategoryId: Number(subCategory?.id) || null,
    dynamicAttributes: ops.map((option) => {
      return {
        field_name: option.field_name,
        option_id: option.id,
      };
    }),
  };
  const seoScreenKey = window.location.href.includes("/find")
    ? EnumScreenNames.FIND_SCREEN
    : EnumScreenNames.SERP_SCREEN;
  const seoPage = await fetchSeoClientSide(
    locale,
    seoScreenKey,
    {
      queryString: qs.parse(
        (window.location.search || "").substring(1, 999999)
      ),
      routerApiResponse,
      postsApiResponse: { _meta: prevState.postsApiResponse._meta },
    },
    abortController__requestSeo
  ).then((seoPageResponse) => {
    if (seoScreenKey === EnumScreenNames.FIND_SCREEN) {
      seoPageResponse.link2 = window.location.href.replace(
        `/${getLang()}`,
        `/${getLang2()}`
      );
    }
    return seoPageResponse;
  });

  prevState.assets = {
    ...prevState.assets,
    seoPage: { ...seoPage },
  };
  return prevState;
};
